import { useEffect, useMemo, useState } from "react";
import { Plus, Trash2, ShieldCheck, FileDown, Search, ClipboardCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { api } from "@/lib/api";
import { formatDateEU, todayISO } from "@/lib/constants";

const CHECKLIST = [
  { key: "surfaces_cleaned", label: "Surfaces cleaned" },
  { key: "equipment_sanitized", label: "Equipment sanitized" },
  { key: "personal_hygiene_verified", label: "Personal hygiene verified" },
  { key: "hand_washing_completed", label: "Hand washing completed" },
];

const blank = () => ({
  check_date: todayISO(),
  check_time: new Date().toTimeString().slice(0, 5),
  user: "",
  linked_batch_id: "",
  surfaces_cleaned: false,
  equipment_sanitized: false,
  personal_hygiene_verified: false,
  hand_washing_completed: false,
  workspace_condition: "OK",
  notes: "",
  issues_detected: "",
  corrective_action: "",
});

export default function HygieneChecks() {
  const [items, setItems] = useState([]);
  const [batches, setBatches] = useState([]);
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(blank());
  // Filters
  const [fBatch, setFBatch] = useState("");
  const [fUser, setFUser] = useState("");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");

  const load = async () => {
    const [h, b] = await Promise.all([api.listHygieneChecks(), api.listProductionBatches()]);
    setItems(h);
    setBatches(b);
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    return items.filter((i) => {
      const batchVal = i.linked_batch_id || i.batch_id || "";
      const userVal = i.user || i.created_by || "";
      const dateVal = i.check_date || "";
      if (fBatch && !batchVal.toLowerCase().includes(fBatch.toLowerCase())) return false;
      if (fUser && !userVal.toLowerCase().includes(fUser.toLowerCase())) return false;
      if (fFrom && dateVal < fFrom) return false;
      if (fTo && dateVal > fTo) return false;
      return true;
    });
  }, [items, fBatch, fUser, fFrom, fTo]);

  const save = async () => {
    try {
      await api.createHygieneCheck(form);
      toast.success("Hygiene check saved");
      setOpen(false);
      setForm(blank());
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this check?")) return;
    try { await api.deleteHygieneCheck(id); toast.success("Deleted"); load(); }
    catch { toast.error("Failed"); }
  };

  const downloadSingle = async (id) => {
    try { await api.downloadHygienePdf(id); toast.success("PDF downloaded"); }
    catch { toast.error("PDF failed"); }
  };

  const downloadRange = async () => {
    try {
      const params = {};
      if (fFrom) params.from_date = fFrom;
      if (fTo) params.to_date = fTo;
      if (fBatch) params.batch_id = fBatch;
      if (fUser) params.user = fUser;
      await api.downloadHygieneRangePdf(params);
      toast.success("Range PDF downloaded");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No matching records");
    }
  };

  return (
    <div data-testid="page-hygiene">
      <PageHeader
        overline="Omavalvonta · Food Safety"
        title="Hygiene"
        subtitle="One record per batch. Inspection-ready PDF export."
        actions={
          <div className="flex gap-2 flex-wrap">
            <Button variant="outline" onClick={downloadRange} data-testid="download-range-pdf">
              <FileDown size={16} className="mr-2" /> Export Range PDF
            </Button>
            <Button onClick={() => { setForm(blank()); setOpen(true); }} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="add-hygiene">
              <Plus size={16} className="mr-2" /> New Check
            </Button>
          </div>
        }
      />

      {/* Filters */}
      <div className="card-soft p-4 mb-5 grid grid-cols-2 md:grid-cols-4 gap-3">
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]" />
          <Input value={fBatch} onChange={(e) => setFBatch(e.target.value)} placeholder="Filter by batch" className="pl-9 font-mono text-xs" data-testid="filter-batch" />
        </div>
        <div className="relative">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#6C757D]" />
          <Input value={fUser} onChange={(e) => setFUser(e.target.value)} placeholder="Filter by user" className="pl-9 text-xs" data-testid="filter-user" />
        </div>
        <Input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} className="text-xs" data-testid="filter-from" />
        <Input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} className="text-xs" data-testid="filter-to" />
      </div>

      {filtered.length === 0 ? (
        <EmptyState icon={ClipboardCheck} title="No hygiene checks" description="Log a check before/after each production batch." />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
          {filtered.map((i) => {
            const checks = CHECKLIST.map((c) => i[c.key]);
            const passed = checks.filter(Boolean).length;
            const total = checks.length;
            const wsOk = (i.workspace_condition || "OK") === "OK";
            const allOk = passed === total && wsOk;
            const batchId = i.linked_batch_id || i.batch_id || "";
            const userVal = i.user || i.created_by || "";
            return (
              <div key={i.id} className="card-soft p-5 flex flex-col gap-2" data-testid={`hygiene-card-${i.id}`}>
                <div className="flex justify-between items-start">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-[0.2em] text-[#6C757D] font-bold">
                      {formatDateEU(i.check_date)} · {i.check_time || "—"}
                    </div>
                    {batchId && <div className="font-mono text-sm text-[#E85D04] font-bold truncate">{batchId}</div>}
                    {userVal && <div className="text-xs text-[#6C757D]">By {userVal}</div>}
                  </div>
                  <div className={`px-2.5 py-1 rounded-full text-[10px] font-bold ${allOk ? "bg-[#1F4D3A]/10 text-[#1F4D3A]" : "bg-[#D62828]/10 text-[#D62828]"}`}>
                    {allOk ? "COMPLETE" : "ISSUES"}
                  </div>
                </div>
                <div className="text-xs space-y-1">
                  {CHECKLIST.map((c) => (
                    <div key={c.key} className="flex justify-between">
                      <span className="text-[#6C757D]">{c.label}</span>
                      <span className={`font-bold ${i[c.key] ? "text-[#1F4D3A]" : "text-[#D62828]"}`}>{i[c.key] ? "YES" : "NO"}</span>
                    </div>
                  ))}
                  <div className="flex justify-between">
                    <span className="text-[#6C757D]">Workspace</span>
                    <span className={`font-bold ${wsOk ? "text-[#1F4D3A]" : "text-[#D62828]"}`}>{i.workspace_condition || "OK"}</span>
                  </div>
                </div>
                {i.issues_detected && (
                  <div className="text-xs bg-[#D62828]/5 border border-[#D62828]/20 rounded-md p-2 text-[#222]">
                    <span className="text-[10px] uppercase tracking-wider text-[#D62828] font-bold">Issue:</span> {i.issues_detected}
                  </div>
                )}
                <div className="flex justify-end gap-1 border-t border-[#E5E5E5] pt-2 mt-1">
                  <Button variant="ghost" size="sm" onClick={() => downloadSingle(i.id)} className="text-[#1F4D3A]" data-testid={`pdf-hygiene-${i.id}`}>
                    <FileDown size={14} className="mr-1" /> PDF
                  </Button>
                  <Button variant="ghost" size="sm" onClick={() => del(i.id)} className="text-[#E76F51] hover:bg-[#E76F51]/10">
                    <Trash2 size={14} />
                  </Button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-white max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="hygiene-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl flex items-center gap-2">
              <ShieldCheck size={20} className="text-[#E85D04]" /> New Production Hygiene Check
            </DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <Field label="Date">
              <Input type="date" value={form.check_date} onChange={(e) => setForm({ ...form, check_date: e.target.value })} data-testid="hygiene-date" />
            </Field>
            <Field label="Time">
              <Input type="time" value={form.check_time} onChange={(e) => setForm({ ...form, check_time: e.target.value })} data-testid="hygiene-time" />
            </Field>
            <Field label="User (responsible)">
              <Input value={form.user} onChange={(e) => setForm({ ...form, user: e.target.value })} placeholder="Name" data-testid="hygiene-user" />
            </Field>
            <Field label="Linked Batch *">
              <Select value={form.linked_batch_id || "none"} onValueChange={(v) => setForm({ ...form, linked_batch_id: v === "none" ? "" : v })}>
                <SelectTrigger data-testid="hygiene-batch"><SelectValue placeholder="Select batch" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {batches.slice(0, 30).map((b) => (
                    <SelectItem key={b.id} value={b.batch_id}>
                      {b.batch_id} · {b.product}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>

            <div className="col-span-2 pt-2 border-t border-[#E5E5E5]">
              <div className="text-[10px] uppercase tracking-[0.2em] text-[#1F4D3A] font-bold mb-2">Checklist</div>
              <div className="space-y-1.5">
                {CHECKLIST.map((c) => (
                  <label key={c.key} className="flex items-center gap-3 cursor-pointer p-2 rounded hover:bg-[#F7F1E8]">
                    <Checkbox
                      checked={!!form[c.key]}
                      onCheckedChange={(v) => setForm({ ...form, [c.key]: !!v })}
                      data-testid={`check-${c.key}`}
                      className="data-[state=checked]:bg-[#1F4D3A] data-[state=checked]:border-[#1F4D3A]"
                    />
                    <span className="text-sm">{c.label}</span>
                  </label>
                ))}
                <div className="flex items-center gap-3 p-2">
                  <span className="text-sm flex-1">Workspace condition</span>
                  <div className="flex gap-1">
                    <Button type="button" size="sm" variant={form.workspace_condition === "OK" ? "default" : "outline"}
                      className={form.workspace_condition === "OK" ? "bg-[#1F4D3A] text-white" : ""}
                      onClick={() => setForm({ ...form, workspace_condition: "OK" })} data-testid="ws-ok">OK</Button>
                    <Button type="button" size="sm" variant={form.workspace_condition === "Not OK" ? "default" : "outline"}
                      className={form.workspace_condition === "Not OK" ? "bg-[#D62828] text-white" : ""}
                      onClick={() => setForm({ ...form, workspace_condition: "Not OK" })} data-testid="ws-not-ok">Not OK</Button>
                  </div>
                </div>
              </div>
            </div>

            <Field label="Notes" className="col-span-2">
              <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="hygiene-notes" />
            </Field>
            <Field label="Issues detected" className="col-span-2">
              <Textarea rows={2} value={form.issues_detected} onChange={(e) => setForm({ ...form, issues_detected: e.target.value })} data-testid="hygiene-issues" />
            </Field>
            <Field label="Corrective action" className="col-span-2">
              <Textarea rows={2} value={form.corrective_action} onChange={(e) => setForm({ ...form, corrective_action: e.target.value })} data-testid="hygiene-action" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} className="bg-[#E85D04] hover:bg-[#c94b03] text-white" data-testid="save-hygiene">Save</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

const Field = ({ label, children, className = "" }) => (
  <div className={`flex flex-col gap-1.5 ${className}`}>
    <Label className="text-xs uppercase tracking-wider text-[#1F4D3A] font-semibold">{label}</Label>
    {children}
  </div>
);
